import ArrowForwardIosOutlinedIcon from "@mui/icons-material/ArrowForwardIosOutlined";
import { Box, ListItemIcon, Menu, MenuItem, MenuProps } from "@mui/material";
import { useTranslate } from "hooks/useTranslate";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { tokens } from "styles/Tokens";
import { checkUserAccess } from "utils/CommonFunctions";
import { setFeedbackDetails } from "../Snackbar/snackbar.slice";
import { NestedMenuConfiguration } from "./NestedMenu.Types";
import { useNestedMenuContext } from "./NestedMenuContext";
export interface NestedMenuProps extends MenuProps {
  configuration: NestedMenuConfiguration[];
  value?: any;
}

const ITEM_HEIGHT = 48;

const NestedMenu: React.FunctionComponent<NestedMenuProps> = (props) => {
  const dispatch = useDispatch();
  const translate = useTranslate();
  const { value, configuration, onClose, open, ...others } = props;

  const { openMenu, closeAllMenus, closeMenu, findOpenMenu } = useNestedMenuContext();
  const [activeItemIndex, setActiveItemIndex] = useState<number>();

  const handleSubMenuClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    const configKey = event.currentTarget.getAttribute("data-config-key");
    if (configKey) {
      openMenu(configKey, event.currentTarget);
    }
  };

  const handleKeyDown = (event: any) => {
    event.stopPropagation();

    const length = configuration.length;
    // If no item is selected, and any arrow button gets clicked, then we want to select the first item
    if (
      ["ArrowUp", "ArrowRight", "ArrowDown", "ArrowLeft"].includes(event.key) &&
      typeof activeItemIndex === "undefined"
    ) {
      setActiveItemIndex(0);
      return;
    }

    const config = configuration[activeItemIndex!];
    if (event.key === "ArrowDown") {
      if (activeItemIndex! + 1 >= length) {
        setActiveItemIndex(0);
        return;
      }
      setActiveItemIndex(activeItemIndex! + 1);
    } else if (event.key === "ArrowUp") {
      if (activeItemIndex! === 0) {
        setActiveItemIndex(length - 1);
        return;
      }
      setActiveItemIndex(activeItemIndex! - 1);
    } else if (event.key === "ArrowRight" && Array.isArray(config.subMenu)) {
      const target = document.querySelector(`*[data-config-key="${config.label}"]`);
      openMenu(config.label, target!);
    } else if (event.key === "ArrowLeft") {
      closeMenu(config.label, true);
    }
  };

  const onMenuItemClick = (event: any, option: any) => {
    event.stopPropagation();
    closeAllMenus({}, "backdropClick");
    if (!checkUserAccess(option.accessType))
      dispatch(
        setFeedbackDetails({
          shouldShow: true,
          setState: setFeedbackDetails({ shouldShow: false }),
          message: translate("user.restrictAccess")
        })
      );
    else option?.action(value);
  };

  return (
    <Menu {...others} open={open} onClose={closeAllMenus} onKeyDown={handleKeyDown}>
      {configuration.map((config, index) => {
        if (!Array.isArray(config.subMenu) || config.subMenu.length === 0) {
          return (
            <MenuItem
              sx={{
                height: "37px",
                "&.MuiMenuItem-root:hover": {
                  backgroundColor: tokens.Background.General.Color,
                  color: tokens.Text.General.Color
                }
              }}
              className={!checkUserAccess(config?.accessType) ? "disabled-menuitem" : ""}
              key={config.label}
              disabled={checkUserAccess(config?.accessType) ? !config.enabled : false}
              onClick={(event: any) => onMenuItemClick(event, config)}
            >
              {config.icon && <config.icon />}
              <Box component={"span"} marginLeft="8px">
                {config?.label}
              </Box>
            </MenuItem>
          );
        }

        const openMenuConfig = findOpenMenu(config.label);
        const isOpen = typeof openMenuConfig === "object";

        return [
          <MenuItem
            sx={{
              height: "37px",
              paddingRight: 0,
              display: "flex",
              "&.MuiMenuItem-root:hover": {
                backgroundColor: tokens.Background.General.Color,
                color: tokens.Text.General.Color
              }
            }}
            className={!checkUserAccess(config?.accessType) ? "disabled-menuitem" : ""}
            key={config.label}
            disabled={checkUserAccess(config?.accessType) ? !config.enabled : false}
            selected={index === activeItemIndex}
            data-config-key={config.label}
            onClick={handleSubMenuClick}
          >
            {config.icon && <config.icon />}
            <Box component={"span"} marginLeft="8px">
              {config?.label}
            </Box>
            <ListItemIcon sx={{ marginLeft: "auto" }}>
              <ArrowForwardIosOutlinedIcon
                className="icon-svg"
                sx={{
                  height: "15px"
                }}
              />
            </ListItemIcon>
          </MenuItem>,
          <NestedMenu
            PaperProps={{
              sx: {
                marginY: "8px",
                borderRadius: "8px",
                maxHeight: ITEM_HEIGHT * 4.5,
                width: "max-content",
                color: tokens.Text.General.PrimaryBody,
                fontVariant: "body2",
                "& .MuiMenuItem-root:hover": {
                  backgroundColor: tokens.Background.General.Color,
                  color: `${tokens.Text.General.Color} !important`,
                  ".icon-svg": {
                    stroke: tokens.Text.General.Color
                  }
                }
              }
            }}
            disablePortal
            key={`${config.label}_menu`}
            value={value}
            configuration={config.subMenu}
            open={isOpen}
            anchorEl={isOpen ? openMenuConfig!.target : null}
            anchorOrigin={{ vertical: "center", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            onClose={closeAllMenus}
          />
        ];
      })}
    </Menu>
  );
};

export default NestedMenu;
